'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Testimonial {
  id: number;
  name: string;
  location: string;
  service: string;
  rating: number;
  text: string;
  image: string;
  alt: string;
}

const TestimonialCarousel = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const testimonials: Testimonial[] = [
  {
    id: 1,
    name: 'Sarah Martinez',
    location: 'Scottsdale, AZ',
    service: 'Patio Cleaning',
    rating: 5,
    text: 'Phoenix Patio Pro transformed our sun-damaged patio back to its original beauty. The team was professional, punctual, and the results exceeded our expectations. Highly recommend for anyone in the Phoenix area!',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_10df5a971-1765003957966.png",
    alt: 'Professional woman with long brown hair smiling at camera in modern office setting'
  },
  {
    id: 2,
    name: 'Michael Chen',
    location: 'Tempe, AZ',
    service: 'Pool Deck Restoration',
    rating: 5,
    text: 'After years of monsoon stains and dust buildup, our pool deck looked terrible. Phoenix Patio Pro made it look brand new! They understand desert climate challenges better than anyone.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bd81bcbd-1763299899173.png",
    alt: 'Asian man in navy blue business suit smiling confidently in corporate environment'
  },
  {
    id: 3,
    name: 'Jennifer Thompson',
    location: 'Chandler, AZ',
    service: 'Full Property Service',
    rating: 5,
    text: 'We used Phoenix Patio Pro for our entire outdoor space before hosting a family reunion. They cleaned our patio, driveway, and outdoor furniture. Everything looked amazing and our guests were impressed!',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_117fb6100-1764990439709.png",
    alt: 'Blonde woman in white blouse smiling warmly in bright natural lighting'
  }];


  const handlePrevious = () => {
    setCurrentIndex((prev) => prev === 0 ? testimonials.length - 1 : prev - 1);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => prev === testimonials.length - 1 ? 0 : prev + 1);
  };

  if (!isHydrated) {
    return (
      <div className="bg-card rounded-lg p-8 border border-border">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-muted rounded w-3/4"></div>
          <div className="h-4 bg-muted rounded w-full"></div>
          <div className="h-4 bg-muted rounded w-5/6"></div>
        </div>
      </div>);

  }

  const currentTestimonial = testimonials[currentIndex];

  return (
    <div className="relative">
      <div className="bg-card rounded-lg p-8 border border-border shadow-brand">
        <div className="flex items-start space-x-6 mb-6">
          <div className="flex-shrink-0">
            <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-primary">
              <AppImage
                src={currentTestimonial.image}
                alt={currentTestimonial.alt}
                className="w-full h-full object-cover" />

            </div>
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h4 className="font-headline font-semibold text-lg text-foreground">
                  {currentTestimonial.name}
                </h4>
                <p className="text-sm text-muted-foreground font-body">
                  {currentTestimonial.location}
                </p>
              </div>
              <div className="flex items-center space-x-1">
                {[...Array(currentTestimonial.rating)].map((_, i) =>
                <Icon key={i} name="StarIcon" size={20} className="text-accent" variant="solid" />
                )}
              </div>
            </div>
            <div className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-body font-medium">
              {currentTestimonial.service}
            </div>
          </div>
        </div>

        <blockquote className="text-foreground font-body leading-relaxed">
          "{currentTestimonial.text}"
        </blockquote>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-center space-x-4 mt-6">
        <button
          onClick={handlePrevious}
          className="w-10 h-10 rounded-full border-2 border-border hover:border-primary transition-brand flex items-center justify-center"
          aria-label="Previous testimonial">

          <Icon name="ChevronLeftIcon" size={20} className="text-foreground" />
        </button>

        <div className="flex items-center space-x-2">
          {testimonials.map((_, index) =>
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-2 h-2 rounded-full transition-brand ${
            index === currentIndex ? 'bg-primary w-8' : 'bg-border'}`
            }
            aria-label={`Go to testimonial ${index + 1}`} />

          )}
        </div>

        <button
          onClick={handleNext}
          className="w-10 h-10 rounded-full border-2 border-border hover:border-primary transition-brand flex items-center justify-center"
          aria-label="Next testimonial">

          <Icon name="ChevronRightIcon" size={20} className="text-foreground" />
        </button>
      </div>
    </div>);

};

export default TestimonialCarousel;